// =====================================================
// BUYERMAPGENERATOR.JSX
// Estate Experts — AI Map Generator
// Interior/Exterior Tabs, Dynamic Fields, AI Preview
// Save Suggestions Table | buyermap- prefix
// =====================================================

import React, { useState, useEffect, useContext } from 'react';
import { FaImage, FaTrash, FaCheckCircle, FaTh, FaList, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../../../assets/css/Buyer/dashboard/buyermap/buyermap.css';
import BuyerMasterLayout from 'layouts/BuyerMasterLayout';
import axios from 'axios';
import AppContext from 'context/AppContext';

// ─────────────────────────────────────────
//  DROPDOWN / OPTION LISTS
// ─────────────────────────────────────────
const INTERIOR_ROOMS = ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Home Office', 'Dining Room'];
const EXTERIOR_PLOTS = ['5 Marla', '10 Marla', '1 Kanal'];
const INTERIOR_STYLES = ['Modern', 'Traditional', 'Minimalist'];
const EXTERIOR_STYLES = ['Modern', 'Traditional', 'Minimalist'];
const LANDSCAPING_OPTIONS = ['Garden', 'Swimming Pool', 'Garage'];

// Buyer id kept hardcoded as requested
const EXBUYER_ID = 1;

// ─────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────

const BuyerMap = () => {
  // ── Page / UI state ──
  const [designType, setDesignType] = useState('interior'); // 'interior' | 'exterior'
  const [previewImg, setPreviewImg] = useState(null); // generated fileName from createDesign (not yet saved)
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState([]); // list from backend (showSuggestions)
  const [toast, setToast] = useState({ show: false, msg: '', type: '' });
  const { baseUrl, authToken, baseUrlForFiles } = useContext(AppContext);

  // ── Individual field states (shared/used across both forms) ──
  const [room, setRoom] = useState('');
  const [style, setStyle] = useState('');
  const [description, setDescription] = useState('');
  const [plotSize, setPlotSize] = useState('');
  const [landScaping, setLandScaping] = useState([]); // array of selected checkboxes

  // Load saved designs from the backend (no localStorage anymore)
  useEffect(() => {
    fetchSuggestions();
  }, []);

  // ── Helpers ──
  const showToast = (msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: '', type: '' }), 2800);
  };

  const resetFields = () => {
    setRoom('');
    setStyle('');
    setDescription('');
    setPlotSize('');
    setLandScaping([]);
  };

  const handleTabSwitch = (tab) => {
    setDesignType(tab);
    setPreviewImg(null);
    resetFields();
  };

  // ── Landscaping checkbox toggle ──
  const toggleLandscaping = (item) => {
    setLandScaping(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  // ── Required-preference check (controls Get Suggestions button) ──
  const canGenerate =
    designType === 'interior'
      ? Boolean(room && style)
      : Boolean(plotSize && style);

  // ── Fetch saved suggestions list from backend ──
  const fetchSuggestions = () => {
    axios.get(`${baseUrl}/buyer/design/showSuggestions`, {
      headers: { Authorization: `Bearer ${authToken}` },
      params: { exbuyer_id: EXBUYER_ID },
    })
      .then((response) => {
        if (response.data.success) {
          // Backend column is "type" (not design_type) and "imagePath" (not img)
          setSavedDesigns(response.data.suggestions || []);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── Generate AI Suggestion (calls backend, does NOT save to DB anymore) ──
  const handleGenerate = () => {
    if (!canGenerate) {
      showToast('⚠️ Please select all required preferences first!', 'error');
      return;
    }

    setGenerating(true);
    setPreviewImg(null);

    const formData = new FormData();
    formData.append('exbuyer_id', EXBUYER_ID);
    formData.append('style', style);
    formData.append('design_type', designType); // matches $request->design_type in backend
    formData.append('room', room);
    formData.append('description', description);
    formData.append('plot_size', plotSize);
    landScaping.forEach(item => formData.append('landscaping[]', item)); // keep as real array, not a stringified one

    axios.post(`${baseUrl}/buyer/design/createDesign`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        setGenerating(false);
        const fileName = response.data.data; // this is the generated fileName returned by createDesign
        setPreviewImg(fileName);
        console.log(`${baseUrlForFiles}/${fileName}`);
      })
      .catch((error) => {
        setGenerating(false);
        console.log(error);
        showToast('⚠️ Failed to generate design', 'error');
      });
  };

  // ── Save Current Suggestion (this is what actually creates the DB record now) ──
  const handleSave = () => {
    if (!previewImg) {
      showToast('⚠️ No design to save. Generate one first!', 'error');
      return;
    }

    setSaving(true);

    const formData = new FormData();
    formData.append('exbuyer_id', EXBUYER_ID);
    formData.append('style', style);
    formData.append('design_type', designType);
    formData.append('room', room);
    formData.append('description', description);
    formData.append('plot_size', plotSize);
    landScaping.forEach(item => formData.append('landscaping[]', item));
    formData.append('img', previewImg); // backend reads $request->img and stores it as imagePath

    axios.post(`${baseUrl}/buyer/design/saveSuggestions`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        setSaving(false);
        fetchSuggestions(); // refresh table from backend
        showToast('💾 Design saved successfully!', 'success');
      })
      .catch((error) => {
        setSaving(false);
        console.log(error);
        showToast('⚠️ Failed to save design', 'error');
      });
  };

  // ── Delete Saved Design ──
  const handleDelete = (id) => {
    const formData = new FormData();
    formData.append('exbuyer_id', EXBUYER_ID);
    formData.append('id', id); // was missing before — backend needs this to know which row to delete

    axios.post(`${baseUrl}/buyer/design/deleteSuggestions`, formData, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        // setSavedDesigns(prev => prev.filter(d => d.id !== id)); // update UI immediately
        fetchSuggestions();
        showToast('🗑️ Design deleted', 'success');
      })
      .catch((error) => {
        console.log(error);
        showToast('⚠️ Failed to delete design', 'error');
      });
  };

  // ── Load a saved design (from already-fetched list) back into the form ──
  const loadSavedDesign = (design) => {
    setPreviewImg(design.imagePath);
    setDesignType(design.type); // backend column is "type"
    setRoom(design.room || '');
    setStyle(design.style || '');
    setDescription(design.description || '');
    setPlotSize(design.plot_size || '');
    setLandScaping(design.landScaping ? design.landScaping.split(', ').filter(Boolean) : []);
  };

  // ─────────────────────────────────────────
  //  RENDER INTERIOR OPTIONS
  // ─────────────────────────────────────────
  const renderInteriorOptions = () => (
    <>
      {/* Select Room */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Select Room:</label>
        <div className="buyermap-btn-group">
          {INTERIOR_ROOMS.map(r => (
            <button
              key={r}
              className={`buyermap-room-btn ${room === r ? 'active' : ''}`}
              onClick={() => setRoom(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Style Preference */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Style Preference:</label>
        <select
          className="buyermap-select"
          value={style}
          onChange={e => setStyle(e.target.value)}
        >
          <option value="">Select style</option>
          {INTERIOR_STYLES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Description:</label>
        <textarea
          className="buyermap-textarea"
          placeholder="Describe your preferred interior style..."
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </div>
    </>
  );

  // ─────────────────────────────────────────
  //  RENDER EXTERIOR OPTIONS
  // ─────────────────────────────────────────
  const renderExteriorOptions = () => (
    <>
      {/* Plot Size */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Plot Size:</label>
        <select
          className="buyermap-select"
          value={plotSize}
          onChange={e => setPlotSize(e.target.value)}
        >
          <option value="">Select plot size</option>
          {EXTERIOR_PLOTS.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* House Style */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Style Preference:</label>
        <select
          className="buyermap-select"
          value={style}
          onChange={e => setStyle(e.target.value)}
        >
          <option value="">Select style</option>
          {EXTERIOR_STYLES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Landscaping */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Landscaping:</label>
        <div className="buyermap-checkbox-group">
          {LANDSCAPING_OPTIONS.map(item => (
            <label key={item} className="buyermap-checkbox-label">
              <input
                type="checkbox"
                checked={landScaping.includes(item)}
                onChange={() => toggleLandscaping(item)}
              />
              {item}
            </label>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="buyermap-form-section">
        <label className="buyermap-form-label">Description:</label>
        <textarea
          className="buyermap-textarea"
          placeholder="Describe your preferred exterior design..."
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </div>
    </>
  );

  // ─────────────────────────────────────────
  //  MAIN RENDER
  // ─────────────────────────────────────────
  return (
    <BuyerMasterLayout pageTitle='AI Suggestions'>
      <div className="buyermap-page">
        {/* ── PAGE HEADER ── */}
        <div className="buyermap-header"></div>

        {/* ── TAB SWITCHER ── */}
        <div className="buyermap-tabs">
          <button
            className={`buyermap-tab ${designType === 'interior' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('interior')}
          >
            interior
          </button>
          <button
            className={`buyermap-tab ${designType === 'exterior' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('exterior')}
          >
            exterior
          </button>
        </div>

        {/* ── MAIN LAYOUT: SIDEBAR + PREVIEW ── */}
        <div className="buyermap-body">

          {/* LEFT SIDEBAR: Options */}
          <div className="buyermap-sidebar">
            <h3 className="buyermap-sidebar-title">
              {designType} Options
            </h3>

            {designType === 'interior' ? renderInteriorOptions() : renderExteriorOptions()}
          </div>

          {/* RIGHT PREVIEW PANEL */}
          <div className="buyermap-preview">
            <div className="buyermap-preview-head">
              <div className="buyermap-preview-title">Preview</div>
              <div className="buyermap-preview-subtitle">AI-Generated Design</div>
            </div>

            {/* Image or Empty State */}
            <div className="buyermap-preview-img-wrap">
              {previewImg ? (
                <img src={`${baseUrlForFiles}/${previewImg}`} alt="AI Generated Design" />
              ) : (
                <div className="buyermap-empty">
                  <span className="buyermap-empty-icon">🏠</span>
                  <div className="buyermap-empty-title">No Design Generated Yet</div>
                  <div className="buyermap-empty-text">
                    {canGenerate
                      ? 'Click "Get Suggestions" to generate AI design'
                      : 'Select all required preferences to enable "Get Suggestions"'}
                  </div>
                </div>
              )}
            </div>

            {/* Get Suggestions Button */}
            <button
              className="buyermap-generate-btn"
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
              title={!canGenerate ? 'Select all required preferences first' : undefined}
            >
              {generating ? (
                <>
                  <div className="buyermap-spinner" />
                  Generating...
                </>
              ) : (
                <>
                  <FaImage /> Get Suggestions
                </>
              )}
            </button>

            {/* Save Suggestion Button (only shows after generation) */}
            {previewImg && !generating && (
              <button className="buyermap-save-btn" onClick={handleSave} disabled={saving}>
                <FaCheckCircle style={{ marginRight: 6 }} />
                {saving ? 'Saving...' : 'Save Suggestion'}
              </button>
            )}
          </div>

        </div>

        {/* ── SAVED DESIGNS SECTION ── */}

        {savedDesigns.length > 0 && (
          <div className="buyermap-saved">
            <h2 className="buyermap-saved-title">Saved Suggestions</h2>

            <div className="buyermap-table-wrap">
              <table className="buyermap-table">
                <thead>
                  <tr>
                    <th className="buyermap-th-checkbox">
                      <input type="checkbox" className="buyermap-checkbox-all" />
                    </th>
                    <th className="buyermap-th-img">
                      <div className="buyermap-th-icons">
                        <button className="buyermap-view-icon active" title="Grid view">
                          <FaTh />
                        </button>
                        <button className="buyermap-view-icon" title="List view">
                          <FaList />
                        </button>
                      </div>
                    </th>
                    <th className="buyermap-th-area">Area</th>
                    <th className="buyermap-th-style">Style</th>
                    <th className="buyermap-th-date">Date Saved</th>
                    <th className="buyermap-th-actions">
                      <FaTrash />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {savedDesigns.map((design, idx) => (
                    <tr
                      key={design.id}
                      className="buyermap-table-row"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      {/* Checkbox */}
                      <td className="buyermap-td-checkbox">
                        <div className="buyermap-radio-wrap">
                          <input
                            type="radio"
                            name="selectedDesign"
                            className="buyermap-radio"
                            onChange={() => loadSavedDesign(design)}
                          />
                        </div>
                      </td>

                      {/* Image */}
                      <td className="buyermap-td-img">
                        <img
                          src={`${baseUrlForFiles}/${design.imagePath}`}
                          alt={`${design.type} design`}
                          className="buyermap-table-img"
                          onClick={() => loadSavedDesign(design)}
                        />
                      </td>

                      {/* Area */}
                      <td className="buyermap-td-area">
                        <div className="buyermap-area-text">
                          {design.type === 'interior' ? design.room : design.plot_size}
                        </div>
                      </td>

                      {/* Style */}
                      <td className="buyermap-td-style">
                        <div className="buyermap-style-text">{design.style}</div>
                      </td>

                      {/* Date Saved */}
                      <td className="buyermap-td-date">
                        <div className="buyermap-date-text">
                          {new Date(design.created_at).toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Delete Button */}
                      <td className="buyermap-td-actions">
                        <button
                          className="buyermap-table-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(design.id);
                          }}
                          title="Delete design"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {savedDesigns.length > 3 && (
              <div className="buyermap-pagination">
                <button className="buyermap-page-btn" disabled>
                  <FaChevronLeft />
                </button>
                <button className="buyermap-page-btn">
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </BuyerMasterLayout>
  );
};

export default BuyerMap;